import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { AnimatedText } from "../components/AnimatedText";
import { LinkWrapper } from "../components/LinkWrapper";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { Icon } from "./Icon";
export const CHIP_SIZES = ["mini", "xs", "sm"];
export const CHIP_COLORS = ["primary", "success", "warning", "info", "highlight"];
const chipVariants = cva("inline-flex box-border items-center", {
  variants: {
    size: {
      mini: "rounded-lg min-h-5 text-xs font-medium px-1.5 gap-1",
      xs: "rounded-[9px] min-h-6 heading-xs px-[9px] gap-1",
      sm: "rounded-xl min-h-8 heading-sm px-3 gap-1.5"
    },
    // Semantic scales (primary/highlight/info/warning) auto-flip in the `.dark`
    // block, so they need no dark variants. `primary` resolves to stone (Figma
    // Badge Grey) via the token layer. `success` is overridden to the emerald
    // palette to match the Figma Badge Green, so it carries dark variants.
    color: {
      primary: cn("bg-primary-50 text-primary-700"),
      success: cn("bg-emerald-50 text-emerald-700", "dark:bg-emerald-950 dark:text-emerald-300"),
      warning: cn("bg-warning-50 text-warning-700"),
      info: cn("bg-info-50 text-info-700"),
      highlight: cn("bg-highlight-50 text-highlight-700")
    }
  },
  defaultVariants: {
    size: "xs",
    color: "primary"
  }
});
const closeIconVariants = {
  primary: cn("text-primary-700 hover:text-primary-900 active:text-primary-950"),
  highlight: cn("text-highlight-700 hover:text-highlight-900 active:text-highlight-950"),
  success: cn("text-emerald-700 hover:text-emerald-900 active:text-emerald-950", "dark:text-emerald-300 dark:hover:text-emerald-100 dark:active:text-emerald-50"),
  warning: cn("text-warning-700 hover:text-warning-900 active:text-warning-950"),
  info: cn("text-info-700 hover:text-info-900 active:text-info-950")
};
const ChipButton = React.forwardRef(({
  icon,
  onClick,
  className,
  size = "xs",
  "aria-label": ariaLabel
}, ref) => /*#__PURE__*/React.createElement("button", {
  ref: ref,
  type: "button",
  onClick: onClick,
  "aria-label": ariaLabel,
  className: cn("rounded-md p-0.5", "transition-colors duration-200 motion-reduce:transition-none", "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring", className)
}, /*#__PURE__*/React.createElement(Icon, {
  visual: icon,
  size: size
})));
ChipButton.displayName = "ChipButton";
// TODO(yuka: 1606): we should update this component so that you cannot have both
// onClick and onRemove at the same time. We should use div when there is no onClick,
// but use button when there is onClick.
// Since we can have a button inside a button with current implementation, the top level element is a div
// with a role="button", a tabIndex={0} to make it focusable, and onKeyDown handler.
/**
 * A compact, mostly read-only label for surfacing a status, category, or short
 * piece of metadata, in several sizes and semantic colors, with an optional leading
 * icon, a breathing "busy" animation, clickable and removable affordances. Use it
 * for statuses, tags, categories, or active filters; for a primary action, use a
 * Button instead.
 * @summary Status or metadata label.
 */
const Chip = React.forwardRef(({
  size,
  color,
  label,
  children,
  className,
  isBusy,
  icon,
  onRemove,
  onClick,
  href,
  ...linkProps
}, ref) => {
  const {
    _: _t
  } = _useLingui();
  const chipContent = /*#__PURE__*/React.createElement("div", {
    className: cn(chipVariants({
      size,
      color
    }), className, onClick && "cursor-pointer"),
    "aria-label": label,
    ref: ref,
    onClick: onClick ? () => onClick() : undefined,
    role: onClick ? "button" : undefined,
    onKeyDown: e => {
      if (onClick && (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
        onClick();
      }
    },
    tabIndex: onClick ? 0 : undefined
  }, children, icon && /*#__PURE__*/React.createElement(Icon, {
    visual: icon,
    size: size === "mini" ? "xs" : size
  }), label && /*#__PURE__*/React.createElement("span", {
    className: cn("grow truncate", onClick && "cursor-pointer")
  }, isBusy ? /*#__PURE__*/React.createElement(AnimatedText, {
    variant: color
  }, label) : label), onRemove && /*#__PURE__*/React.createElement(ChipButton, {
    icon: XClose,
    size: size === "sm" ? "sm" : "xs",
    className: cn("-mr-1", closeIconVariants[color || "primary"]),
    "aria-label": _t(
    /** i18n */
    {
      id: "t_YqKh",
      message: "Remove"
    }),
    onClick: e => {
      e.preventDefault();
      e.stopPropagation();
      onRemove();
    }
  }));
  return href ?
  /*#__PURE__*/
  // we need make the anchor inline but not inline layout to avoid having descender reserve;
  // align-middle keeps the anchor vertically aligned like the non-link chip, on which
  // consumers set vertical alignment via className (inert on the inner div once wrapped)
  React.createElement(LinkWrapper, {
    href: href,
    className: "inline-flex align-middle",
    ...linkProps
  }, chipContent) : chipContent;
});
Chip.displayName = "Chip";
export { Chip };
//# sourceMappingURL=Chip.js.map